/*
 * Copyright (c) 2021 CalAmp Corp.  All Rights Reserved
 */
import React, {SyntheticEvent, useMemo} from 'react';
import {allExpanded, defaultStyles, JsonView} from 'react-json-view-lite';
import {translate} from '@ion/core/src/locale/locale';
import {
  AuditChange,
  AuditChanges,
  Entity
} from '@ion/core/src/types/entities';
import {formatDateTime} from '@ion/core/src/utils/DateUtil';
import {isValidISODateTime} from '@ion/core/src/utils/Validators';
import {useExtendStyles} from '../../hooks/useExtendStyles';
import EditorDialog from '../EditorDialog';
import styles from '../editorStyles';
import {AuditRecordType, AuditTransformOptions} from './AuditGridUtils';
import 'react-json-view-lite/dist/index.css';

export interface ViewAuditChangesDialogProps {
  entity: Entity;
  auditRecord: AuditRecordType;
  onCloseDialog?: (event: SyntheticEvent<{}, Event>) => void;
  transformOptions?: AuditTransformOptions;
}

const overrides = () => ({
  form : {
    height : '100%'
  },
  formPaper : {
    minWidth    : '70% !important',
    paddingLeft : '2px !important',
    maxHeight   : '574px',
    height      : '574px'
  }
});

/**
 * @ViewAuditChangesDialog
 *
 * The ViewAuditChangesDialog displays the changes of a selected audit record
 * as a JSON tree, applying the `transformChanges` of the given audit options.
 *
 * ```typescript
 *   import ViewAuditChangesDialog from '@ion/dls/src/editors/audit/ViewAuditChangesDialog';
 *   ...
 *
 *   <ViewAuditChangesDialog auditRecord={auditRecord}
 *     entity={Entity.vescout}
 *     transformOptions={SCOUT_AUDIT_OPTIONS}
 *     onCloseDialog={onCloseAuditChanges} />
 *
 * ```
 */
export const ViewAuditChangesDialog = ({
  entity, auditRecord, onCloseDialog, transformOptions
}: ViewAuditChangesDialogProps) => {
  const classes = useExtendStyles(styles, overrides)();

  const getTitleName = function(): string {
    if (!auditRecord) {
      return translate(`common:editors.entityLabels.${entity}`);
    }
    return auditRecord.user || auditRecord.username;
  };

  const auditChanges = useMemo(() => {
    const changes            = formatChangesDateTimes(auditRecord?.changes);
    const {transformChanges} = transformOptions || {};
    return transformChanges ? transformChanges(changes) : changes;
  }, [auditRecord, transformOptions]);

  return (
    <EditorDialog open
      dialogTitle={translate('common:editors.audit.title', {name: getTitleName()})}
      onCloseDialog={onCloseDialog}
      onCancelDialog={onCloseDialog}
      styleOverrides={overrides}
      omitActions>
      <form className={classes.form}>
        <pre><JsonView data={auditChanges || ''} shouldExpandNode={allExpanded} style={defaultStyles} /></pre>
      </form>
    </EditorDialog>
  );
};

/**
 * Formats the date-time values of the given audit change.
 * @param change the audit change to format
 * @returns the formatted audit change
 */
const formatChangeDateTimes = (change: AuditChange): AuditChange => {
  const newChange = {...change};
  newChange.newValue = formatDateTime(change?.newValue as string);
  newChange.oldValue = formatDateTime(change?.oldValue as string);
  return newChange;
};

/**
 * Checks the fields of the audit change to see if they are a date-time.
 * @returns true if either value of the audit change is a date-time, false if neither are.
 */
const isDateTime = ({newValue, oldValue}: AuditChange): boolean => {
  return isValidISODateTime(newValue) || isValidISODateTime(oldValue);
};

/**
 * Checks each given audit change and if it's a date-time, will properly format it.
 * @param changes the array of all audit changes
 * @returns a new array of changes but with any date-time fields formatted
 */
const formatChangesDateTimes = (changes: AuditChanges): AuditChanges => {
  if (changes) {
    return changes.map(change => isDateTime(change) ? formatChangeDateTimes(change) : change);
  }
  return [];
};

export default ViewAuditChangesDialog;
